import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Dialog } from '@mui/material';
import { max } from 'date-fns';

interface StyledDialogProps {
  width?: string;
}

export const StyledDialog = styled(Dialog)<StyledDialogProps>(({ width }) => ({
  '& .MuiDialog-container': {
    justifyContent: 'center',
    alignItems: 'center',
  },
  '& .MuiDialog-paper': {
    width: width || '565px',
    height: '600px',
    maxWidth: 'none',
    maxHeight: '90vh',
    margin: 0,
    overflow: 'hidden',
    borderRadius: '16px',
    padding: '48px 64px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export const RelativePositionBox = styled(Box)({
  position: 'relative',
});

export const CloseIcon = styled(Box)({
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  fontSize: '24px',
  cursor: 'pointer',
  color: 'var(--instructions)',
  '&:hover': {
    color: 'var(--error)',
  },
}) as typeof Box;

export const FormContainer = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflow: 'hidden',
  minHeight: 0,
});

export const HeaderBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
});

export const AlertIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'iconColor',
})<{ iconColor?: string }>(({ iconColor = 'var(--success)' }) => ({
  fontSize: '24px',
  color: iconColor,
  marginRight: '12px',
}));

export const TitleTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '20px',
  fontWeight: 600,
  color: 'var(--primary-navy)',
});

export const TitleTypographyBlue = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontSize: '32px',
  lineHeight: '100%',
  color: 'var(--primary-blue)',
});

export const SubtitleTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 400,
  color: 'var(--subtext)',
  marginBottom: '4px',
});

export const SubtitleSecondary = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '12px',
  fontWeight: 400,
  color: 'var(--instructions)',
  marginBottom: '20px',
});

export const LabelTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '12px',
  fontWeight: 400,
  color: 'var(--instructions)',
});

export const ValueTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--primary-navy)',
});

export const SectionTitleTypography = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--subtext)',
  marginBottom: 16,
});

export const ButtonContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
  marginTop: '24px',
});

export const ButtonContainerAddGrnPay = styled(Box)({
  display: 'flex',
  gap: '12px',
  marginTop: '90px',
});

export const ButtonContainerAddGrn = styled(Box)({
  display: 'flex',
  gap: '12px',
  marginTop: '24px',
  marginBottom: '16px',
  marginLeft: '40px',
});

export const PrimaryButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<{ bgColor?: string }>(({ bgColor = 'var(--primary-blue)' }) => ({
  backgroundColor: bgColor,
  color: 'white',
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 24px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: bgColor,
    filter: 'brightness(0.9)',
  },
  '&.Mui-disabled': {
    backgroundColor: bgColor,
    opacity: 0.5,
    color: 'white',
  },
}));

export const CancelButton = styled(Button)({
  backgroundColor: 'var(--inactive-btn)',
  color: 'var(--primary-navy)',
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 24px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: 'var(--hover-white)',
  },
});

export const FormContentBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const ScrollableFormContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: '8px',
  minHeight: 0,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
});

export const BorderedSectionBox = styled(Box)({
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '16px',
});

export const StatusInfoBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'borderColor',
})<{ borderColor?: string }>(({ borderColor = 'var(--inactive-border)' }) => ({
  border: `1px solid ${borderColor}`,
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
}));

export const HeaderBoxSimple = styled(Box)({
  marginBottom: '20px',
});

export const TitleTypographyLarge = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '32px',
  fontWeight: 600,
  color: 'var(--primary-blue)',
});

export const SubtitleTypographySimple = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 400,
  color: 'var(--subtext)',
});

export const SubmitButton = styled('button')({
  height: '40px',
  backgroundColor: 'var(--primary-blue)',
  color: 'white',
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 24px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'var(--primary-blue)',
    filter: 'brightness(0.9)',
  },
  '&:disabled': {
    backgroundColor: 'var(--primary-blue)',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

export const StyledCircularProgress = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiCircularProgress-root': {
    color: 'white',
  },
});

export const DetailLabelText = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 400,
  color: 'var(--instructions)',
});

export const DetailValueText = styled(Typography)({
  fontFamily: 'Outfit',
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--primary-navy)',
});

export const DateLabel = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  display: 'block',
  fontSize: '16px',
  color: 'var(--subtext)',
  marginBottom: '16px',
});

export const ViewStyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    position: 'fixed',
    right: 0,
    top: '100px',
    bottom: 0,
    width: '940px',
    height: 'auto',
    margin: 0,
    maxWidth: 'none',
    maxHeight: 'none',
    borderRadius: 0,
    background: 'var(--white)',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const FixedHeaderWrapper = styled(Box)({
  position: 'sticky',
  top: 0,
  background: 'var(--white)',
  zIndex: 10,
  padding: '20px 32px 0',
  flexShrink: 0,
});

export const HeaderSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBottom: '16px',
  flexShrink: 0,
});

export const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const ViewCloseIcon = styled(Box)({
  width: '24px',
  height: '24px',
  color: 'var(--instructions)',
  cursor: 'pointer',
  fontSize: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    color: 'var(--secondary-navy)',
  },
}) as typeof Box;

export const ContentWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '10px 32px 20px',

  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
});

export const MainContentGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 325px',
  gap: '16px',
  alignItems: 'start',
});

export const PaymentDetailsBox = styled(Box)({
  background: 'var(--readonly-bg)',
  borderRadius: '8px',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const PaymentInfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

export const InfoBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const InfoBlockRight = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '8px',
});

// White inner card for payment method details
export const PaymentMethodCard = styled(Box)({
  background: 'var(--white)',
  borderRadius: '4px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
});

export const PaymentMethodRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
  '&:first-of-type': {
    paddingTop: '0',
  },
  '&:last-of-type': {
    paddingBottom: '0',
  },
});

export const PaymentMethodDivider = styled(Box)({
  width: '100%',
  height: '0px',
  borderTop: '1px solid var(--hover-white)',
});

export const StatusInfoRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

// Rejection Note Box (with error background)
export const RejectionNoteBox = styled(Box)({
  background: 'rgba(232, 94, 81, 0.08)',
  borderRadius: '4px',
  padding: '8px 12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const RejectionLabelTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '100%',
  color: 'var(--rejection-text)',
});

export const SmallValueTypography = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '100%',
  color: 'var(--secondary-navy)',
});

export const NoteText = styled(Typography)({
  fontFamily: "'Outfit', sans-serif",
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '140%',
  color: 'var(--secondary-navy)',
});
